import { MAX_CREDIBLE_CUT_SHARE as CUT_SHARE } from './priceCredibility';
import type { Listing } from '@/types/listing';
import type { MarketStats } from './api';

/**
 * The curated home feed ("FYP") — which rails exist, what backs them, and the
 * data sanity they need before they're allowed on screen.
 *
 * Every rail is real MLS data or it does not render. There is **no historical
 * price series** in this product, so nothing here draws a trend, a sparkline, or
 * a "prices are up" claim: the market rail reports current medians only. (W14)
 */

/* ------------------------------------------------------------------ *
 * Degenerate $/sqft
 * ------------------------------------------------------------------ */

/**
 * A "best value by $/sqft" rail sorted `ppsf_asc` puts the feed's worst data
 * first, not its best homes. The live feed contains an **$18,500 listing at
 * $3.99/sqft** — that is a bad record (a land parcel mislabelled, a typo'd
 * price, a lease), not a bargain, and a rail led by it reads as broken.
 *
 * These bounds are deliberately loose: they exclude the physically impossible,
 * not the merely cheap. The cheapest genuine Bay Area condo is nowhere near
 * $50/sqft, and no real home in this market is priced under $100k.
 */
export const MIN_CREDIBLE_PRICE = 100_000;
export const MIN_CREDIBLE_PPSF = 100;
/** Above this, `pricePerSqft` is almost certainly `price / a tiny sqft typo`. */
export const MAX_CREDIBLE_PPSF = 5_000;
export const MIN_CREDIBLE_SQFT = 200;

/**
 * Is this listing's $/sqft trustworthy enough to rank *by*?
 *
 * Deliberately stricter than "should we show this home at all" — a home with odd
 * numbers is fine in a Just-listed rail, where nothing is being claimed about
 * its value. It is only disqualifying when $/sqft is the ranking.
 */
export function hasCredibleValueSignal(l: Listing): boolean {
  const ppsf = l.pricePerSqft ?? 0;
  return (
    l.price >= MIN_CREDIBLE_PRICE &&
    (l.sqft ?? 0) >= MIN_CREDIBLE_SQFT &&
    ppsf >= MIN_CREDIBLE_PPSF &&
    ppsf <= MAX_CREDIBLE_PPSF
  );
}

/** The value rail: credible homes only, cheapest $/sqft first. */
export function bestValueRail(listings: Listing[], limit = 12): Listing[] {
  return listings
    .filter(hasCredibleValueSignal)
    .sort((a, b) => (a.pricePerSqft ?? 0) - (b.pricePerSqft ?? 0))
    .slice(0, limit);
}

/* ------------------------------------------------------------------ *
 * Price cuts
 * ------------------------------------------------------------------ */

/** Dollars off the original list price, or 0 when there is no real cut. */
export function priceCutAmount(l: Listing): number {
  const original = l.originalListPrice ?? 0;
  if (original <= 0 || l.price <= 0 || original <= l.price) return 0;
  return original - l.price;
}

/**
 * Ranking by "biggest cut" has the same failure mode as ranking by "lowest
 * $/sqft": it sorts the feed's typos to the front. Measured on the live feed, a
 * $24,950,000 "home" recorded as `sqft: 1` and a $3,950,000 one bedroom, also at
 * `sqft: 1`, both show reductions past half the asking price. A buyer led with
 * "$12,950,000 off" is being shown a data error dressed as the deal of the
 * century.
 *
 * The bound itself now lives in one place — see `@/lib/priceCredibility`, which
 * records the measurement and why this file's previous 0.5 was replaced by the
 * 60 the notifier already used. Both records above are caught by
 * MIN_CREDIBLE_SQFT below regardless, which is the guard doing the real work
 * here.
 */
export { MAX_CREDIBLE_CUT_SHARE } from './priceCredibility';

/**
 * The ceiling this page is built for.
 *
 * Sorting credible cuts by dollars alone put **"up to $4.4M off"** at the top of
 * the feed — real, verifiable, and about a home nobody reading this page can buy.
 * `/` is written for the first-time Bay Area buyer in `docs/USER_STORIES.md`
 * (Priya & Sam, roughly $1.1–1.4M), and a rail whose headline number comes from
 * ultra-luxury inventory tells that buyer the page is not about them.
 *
 * $2.5M rather than $1.4M because the band should cover the whole page's audience
 * including the stretch case, not just the median one — at $1.4M the rail thins to
 * the cheapest end of the market and stops being a browse.
 *
 * Deliberately a fixed number rather than the visitor's own budget: `/` is
 * ISR-cached (`revalidate = 300`) and the rails ship in the HTML, so reading a
 * per-visitor band would either make the page dynamic — losing the cache that
 * gives it its LCP — or re-band after hydration, which is the 0.000 → 0.0252 CLS
 * that R132 paid for. The visitor's own band drives "Picked for you", which is a
 * client island by construction. (W14 R138)
 */
export const PERSONA_PRICE_CEILING = 2_500_000;

export function hasCredibleCut(l: Listing): boolean {
  const cut = priceCutAmount(l);
  if (cut <= 0) return false;
  if (l.price < MIN_CREDIBLE_PRICE) return false;
  // A card reading "1 sqft" reads as broken whatever the discount.
  if ((l.sqft ?? 0) < MIN_CREDIBLE_SQFT) return false;
  return cut / (l.originalListPrice ?? 1) <= CUT_SHARE;
}

/**
 * Biggest *credible* reduction first. A listing whose `originalListPrice` is
 * missing or not above the current price contributes no cut and is dropped — the
 * rail says "price cuts", so every card in it must actually have one.
 */
export function priceCutRail(listings: Listing[], limit = 12): Listing[] {
  return listings
    .filter(hasCredibleCut)
    .sort((a, b) => priceCutAmount(b) - priceCutAmount(a))
    .slice(0, limit);
}

/**
 * The rail's headline: the biggest cut in it, in dollars **and** as a share.
 *
 * Both numbers or neither — the dollars alone are what let the subtitle read "up
 * to $4.4M off", which is not a number a first-time buyer can use. `null` when the
 * rail is empty, so the caller renders no claim rather than "$0 off".
 */
export function priceCutHeadline(listings: Listing[]): { amount: number; pct: number } | null {
  const best = listings.reduce<Listing | null>(
    (top, l) => (top === null || priceCutAmount(l) > priceCutAmount(top) ? l : top),
    null
  );
  if (!best) return null;
  const amount = priceCutAmount(best);
  if (amount <= 0) return null;
  return { amount, pct: Math.round((amount * 100) / (best.originalListPrice ?? amount)) };
}

/* ------------------------------------------------------------------ *
 * Open houses
 * ------------------------------------------------------------------ */

/** ISO date (YYYY-MM-DD) `days` after `todayISO`. */
function shiftISO(todayISO: string, days: number): string {
  const d = new Date(`${todayISO}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The coming Saturday and Sunday, inclusive of today when today is already the
 * weekend. Sunday counts as "this weekend" up to and including Sunday itself; on
 * a Monday the window is the Saturday and Sunday five and six days out.
 */
export function weekendWindow(todayISO: string): { from: string; to: string } {
  const dow = new Date(`${todayISO}T00:00:00`).getDay(); // 0 Sun … 6 Sat
  if (dow === 6) return { from: todayISO, to: shiftISO(todayISO, 1) };
  if (dow === 0) return { from: todayISO, to: todayISO };
  return { from: shiftISO(todayISO, 6 - dow), to: shiftISO(todayISO, 7 - dow) };
}

/**
 * Homes with an open house inside the window. The API's `open_houses_only` only
 * guarantees *an* upcoming open house, which can be weeks out — a rail headed
 * "this weekend" has to check the dates itself or it is making a claim the data
 * doesn't support.
 */
export function openHouseRail(listings: Listing[], todayISO: string, limit = 12): Listing[] {
  const { from, to } = weekendWindow(todayISO);
  return listings
    .filter((l) =>
      (l.openHouses ?? []).some((oh) => {
        // The feed's OpenHouse carries `date` as an RFC3339 timestamp.
        const day = (oh.date ?? '').slice(0, 10);
        return day >= from && day <= to;
      })
    )
    .slice(0, limit);
}

/* ------------------------------------------------------------------ *
 * Market pulse
 * ------------------------------------------------------------------ */

export interface CitySpotlight {
  city: string;
  slug: string;
  medianPrice: number;
  activeTotal: number;
  medianDom: number;
  priceCutShare: number;
}

/**
 * City spotlights, biggest inventory first. A city is only shown when it has a
 * median price and enough active listings for the number to mean anything — the
 * markets endpoint publishes `sample_size`, and a median over three homes is
 * noise dressed as a statistic.
 */
export const MIN_SPOTLIGHT_SAMPLE = 20;

export function citySpotlights(
  markets: MarketStats[],
  slugify: (s: string) => string,
  limit = 6
): CitySpotlight[] {
  return markets
    .filter((m) => m.median_price > 0 && m.sample_size >= MIN_SPOTLIGHT_SAMPLE)
    .sort((a, b) => b.active_total - a.active_total)
    .slice(0, limit)
    .map((m) => ({
      city: m.city,
      slug: slugify(m.city),
      medianPrice: m.median_price,
      activeTotal: m.active_total,
      medianDom: m.median_dom,
      priceCutShare: m.price_cut_share,
    }));
}

/**
 * Headline numbers across covered cities: the median of city medians, the
 * median days since listing, and the share of listings carrying a cut.
 *
 * `medianDom` is the median of the per-city `median_dom`, which the API derives
 * from `list_date` because the MLS feed's DaysOnMarket field is empty for every
 * listing. It is the age of standing inventory, not time-to-sale — every label
 * rendered from it must say "since listed" rather than "on market".
 *
 * `null` when there isn't enough to report. Explicitly **no** change-over-time
 * figure: the API exposes a snapshot, not a series, so any "up 4% this month"
 * would be invented.
 */
export interface MarketPulse {
  medianPrice: number;
  medianDom: number;
  priceCutShare: number;
  cities: number;
  activeTotal: number;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}

export function marketPulse(markets: MarketStats[]): MarketPulse | null {
  const usable = markets.filter((m) => m.median_price > 0 && m.sample_size >= MIN_SPOTLIGHT_SAMPLE);
  if (usable.length < 3) return null;
  return {
    medianPrice: median(usable.map((m) => m.median_price)),
    medianDom: median(usable.map((m) => m.median_dom).filter((d) => d > 0)),
    priceCutShare: median(usable.map((m) => Math.round(m.price_cut_share * 100))) / 100,
    cities: usable.length,
    activeTotal: usable.reduce((sum, m) => sum + m.active_total, 0),
  };
}

/* ------------------------------------------------------------------ *
 * Rail plumbing
 * ------------------------------------------------------------------ */

/**
 * A rail only renders with enough cards to be a rail. One lonely card under a
 * heading like "Open houses this weekend" reads as a bug, and a rail of zero is
 * a void — both hide instead.
 */
export const MIN_RAIL_CARDS = 3;

export function railIsWorthShowing(listings: Listing[]): boolean {
  return listings.length >= MIN_RAIL_CARDS;
}
